import { useEffect } from "react";
import { Clock3, Layers, ArrowUpRight, MessageCircle, CalendarDays, MapPin } from "lucide-react";
import dayjs from "dayjs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useSite } from "@/lib/site";
import { RichText, stripHtml } from "./RichText";

const activeTiers = (event) =>
  (event?.pricing_tiers || []).filter((t) => t.label && (!t.until_date || new Date(t.until_date) > new Date()));

export const EnrollModal = ({ event, open, onClose }) => {
  const { content } = useSite();
  const whatsapp = (content.settings?.whatsapp_number || "").replace(/[^0-9]/g, "");
  const isLive = event?.kind === "event";
  const nextDate = (event?.dates || [])[0];

  useEffect(() => {
    if (open && event?.title) window.__tblTrack?.("course_details_view", { title: event.title });
  }, [open, event?.title]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="enroll-modal" className="flex max-h-[90vh] max-w-md flex-col rounded-3xl border-ink/10 bg-sand p-0 overflow-hidden">
        <div className="shrink-0 bg-ink px-7 py-5 text-sand">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium leading-snug">{event?.title}</DialogTitle>
          </DialogHeader>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-sand/60">
            {isLive && nextDate && (
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="h-3 w-3" />
                {dayjs(nextDate.start || nextDate.date || nextDate).format("D MMM YYYY, h:mm A")}
              </span>
            )}
            {event?.hours && <span className="inline-flex items-center gap-1"><Clock3 className="h-3 w-3" />{event.hours}</span>}
            {!!event?.modules && <span className="inline-flex items-center gap-1"><Layers className="h-3 w-3" />{event.modules} module{event.modules > 1 ? "s" : ""}</span>}
            {event?.event_mode && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3 w-3" />{event.event_mode === "online" ? "Online" : event.location_address || "In person"}
              </span>
            )}
            {!isLive && <span>Self-paced · lifetime access</span>}
          </div>
        </div>

        <div data-lenis-prevent className="flex-1 overflow-y-auto overscroll-contain">
          {(event?.tagline || event?.desc) && (
            <div className="px-7 py-5" data-testid="enroll-event-details">
              {event?.thumbnail && (
                <img src={event.thumbnail} alt={event.title} className="mb-4 aspect-video w-full rounded-xl object-cover" />
              )}
              {event?.tagline && <p className="text-sm font-medium italic text-plumsoft">{stripHtml(event.tagline)}</p>}
              {event?.desc && (
                <RichText
                  html={event.desc}
                  className="mt-2 text-sm leading-relaxed text-plumsoft [&_h3]:mt-4 [&_h3]:font-display [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-ink [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mt-1 [&_p]:mt-2 [&_p:first-child]:mt-0 [&_strong]:font-semibold [&_strong]:text-ink"
                />
              )}
              {activeTiers(event).map((t) => (
                <p key={t.tier_id} className="mt-3 inline-block rounded-full bg-gold/20 px-3 py-1 text-[11px] font-outfit font-semibold text-ink">
                  {t.label}: ₹{Number(t.price).toLocaleString("en-IN")}
                  {t.until_date && ` · till ${dayjs(t.until_date).format("D MMM")}`}
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="shrink-0 space-y-2 border-t border-ink/10 px-7 py-5">
          {event?.book_url ? (
            <a
              data-testid="enroll-propract-cta"
              href={event.book_url}
              onClick={() => window.__tblTrack?.("enroll_click", { title: event?.title || "", price: event?.price || "" })}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-terra py-3.5 font-outfit text-sm font-semibold text-white transition-colors hover:bg-terradark"
            >
              Enroll now {event?.price ? `· ${event.price}` : ""} <ArrowUpRight className="h-4 w-4" />
            </a>
          ) : (
            whatsapp && (
              <a
                data-testid="enroll-whatsapp-fallback"
                href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(`Hi, I'd like to enroll in "${event?.title}".`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-3.5 font-outfit text-sm font-semibold text-white"
              >
                <MessageCircle className="h-4 w-4" /> Enroll via WhatsApp
              </a>
            )
          )}
          <p className="text-center text-[11px] text-faint">
            Secure checkout & instant access on{" "}
            <a data-testid="enroll-propract-link" href="https://propract.com" target="_blank" rel="noopener" className="font-semibold text-terra hover:underline">
              ProPract
            </a>
            .
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
